import React, { useContext, useState } from "react"
import { FormattedMessage } from "react-intl"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faGraduationCap } from "@fortawesome/pro-solid-svg-icons"
import { FirebaseContext } from "../../components/Firebase"
import useUserEmailCredentials from "./useUserEmailCredentials"
import { sendStandardToast } from "../../components/toasts/standard-toast"
import ReauthenticateModal from "./ReauthenticateModal"
import AddPasswordModal from "./AddPasswordModal"

/**
 * Button for linking a student's school credentials to their wingspans account
 * (for example, they sign up as a prospective student at a career fair and later
 * enroll at the school)
 * 
 * @param {boolean} forConfirmation - whether the button's text should indicate
 * confirming a school email that has already been connected to this account, or
 * connecting a new school email to this account 
 * 
 * @returns {JSX}
 */
const ConnectSchoolAccountButton = ({ forConfirmation }) => {
  const { firebase } = useContext(FirebaseContext)
  const { schoolLoginProvider } = useUserEmailCredentials()
  const [reauthModalOpen, setReauthModalOpen] = useState(false)
  const [passwordModalOpen, setPasswordModalOpen] = useState(false)
    
  const handleConnectSchoolAccount = async (e) => {
    if (e) {
      e.preventDefault()
    }
    
    // If a user's personal email isn't verified, then it will be overwritten
    // with their school email when they link their school credential
    if (!firebase.auth.currentUser.emailVerified) {
      sendStandardToast({
        heading: "Before connecting your school email, your personal email must be verified",
        message: `Please click on the link sent to ${firebase.auth.currentUser.email}, then reload this page`,
      })
      try {
        await firebase.auth.currentUser.sendEmailVerification()
      } catch (e) {
        console.error("handleConnectSchoolAccount: error when sending email verification link", e)
        sendStandardToast({
          heading: "Error when sending email verification link",
          message: `${e.message}`,
        })
      }
      return
    }

    // Firebase auth only intends for users to have one credential per provider.
    // If the user's only current credential is google SSO, and their school
    // uses google SSO for student sign ins, then the user needs to pick a
    // password and have the personal email google credential unlinked from
    // their account
    const currentCredentials = firebase.auth.currentUser.providerData
    const hasPassword = currentCredentials.some(p => p.providerId === "password")
    const credentialProvidersOverlap = currentCredentials.some(p => p.providerId === schoolLoginProvider.providerId)
    if (credentialProvidersOverlap && !hasPassword) {
      setPasswordModalOpen(true)
      return
    } else if (credentialProvidersOverlap) {
      try {
        await firebase.auth.currentUser.unlink(schoolLoginProvider.providerId)
      } catch (e) {
        console.error("potential error when unlinking", e)
        if (e.code === "auth/requires-recent-login") {
          setReauthModalOpen(true)
        } else {
          sendStandardToast({
            heading: "Error when trying to preparing to connect school account",
            message: e.message,
          })
        }
        return
      }
    }

    const signupEmail = firebase.auth.currentUser.email
    try {
      await firebase.auth.currentUser.linkWithPopup(schoolLoginProvider)
    } catch (e) {
      console.error("potential error when linking", e)
      if (["auth/popup-blocked", "auth/popup-closed-by-user", "auth/cancelled-popup-request"].includes(e.code)) {
        console.log("ERROR WHEN LINKING:", e)
      } else if (e.code === "auth/requires-recent-login") {
        setReauthModalOpen(true)
      } else {
        sendStandardToast({
          heading: "Error when trying to connecting school account",
          message: e.message,
        })
      }
      return
    }

    const newProvider = firebase.auth.currentUser.providerData.find(p => 
      p.providerId === schoolLoginProvider.providerId && p.email !== signupEmail
    )
    if (newProvider) {
      const newEmail = newProvider.email
      sendStandardToast({
        heading: `Successfully connected to school account: ${newEmail}`,
        message: "You may need to reload the page to see this change reflected",
        type: "success",
      })
    }
  }

  return (
    <>
      <button
        className="account-page-submit-button"
        onClick={handleConnectSchoolAccount}
        type="button"
      >
        <FontAwesomeIcon icon={faGraduationCap} size="lg" />
        {forConfirmation ? (
          <FormattedMessage
            id="account-confirm-school-email"
            defaultMessage="Confirm School Email"
          />
        ) : (
          <FormattedMessage
            id="account-add-school-email"
            defaultMessage="Add School Email"
          />
        )}
      </button>
      <ReauthenticateModal
        show={reauthModalOpen}
        handleClose={() => setReauthModalOpen(false)}
        onReauthSuccess={handleConnectSchoolAccount}
      />
      <AddPasswordModal
        show={passwordModalOpen}
        afterPasswordAdded={handleConnectSchoolAccount}
        handleClose={() => setPasswordModalOpen(false)}
        handleOpen={() => setPasswordModalOpen(true)}
      />
    </>
  )
}

export default ConnectSchoolAccountButton
